import { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useWorkspaceScope } from "@/contexts/useWorkspaceScope";
import { useMetaIntegration } from "@/hooks/useMetaIntegration";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "@/hooks/use-toast";
import {
  Settings as SettingsIcon,
  Link2,
  Users,
  Building2,
  CreditCard,
  Facebook,
  ArrowLeft,
  Check,
  Loader2,
  RefreshCw,
  Wifi,
  FileText,
  Play,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { errorMessage } from "@/lib/errors";

const settingsTabs = [
  { id: "workspace", label: "Workspace", icon: Building2, path: "/app/settings" },
  { id: "team", label: "Team", icon: Users, path: "/app/settings/team" },
  { id: "integrations", label: "Integrations", icon: Link2, path: "/app/settings/integrations" },
  { id: "billing", label: "Billing", icon: CreditCard, path: "/app/settings/billing" },
];
const activeTabPath = "/app/settings/integrations";

export default function IntegrationsMeta() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { activeWorkspaceId } = useWorkspaceScope();
  const {
    config,
    configRows,
    configLoading,
    refetchConfig,
    testConnection,
    loadForms,
    saveConnection,
    syncNow,
    oauthStart,
    subscribePage,
  } = useMetaIntegration(activeWorkspaceId ?? null);

  const [selectedPageId, setSelectedPageId] = useState<string | null>(null);
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [forms, setForms] = useState<{ id: string; name: string }[]>([]);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [pageIdManual, setPageIdManual] = useState("");
  const [pageAccessToken, setPageAccessToken] = useState("");
  const [connectionStatus, setConnectionStatus] = useState<"idle" | "connected" | "error">("idle");
  const [connectionError, setConnectionError] = useState<string | null>(null);

  const selectedPage = selectedPageId
    ? configRows.find((r) => r.page_id === selectedPageId)
    : configRows[0] ?? null;
  const isConnected = configRows.length > 0;
  const lastSyncAt = config?.last_sync_at ? new Date(config.last_sync_at).toLocaleString() : null;

  useEffect(() => {
    if (configRows.length > 0 && !selectedPageId) {
      setSelectedPageId(configRows[0].page_id);
    }
  }, [configRows, selectedPageId]);

  useEffect(() => {
    if (selectedPage) {
      setSelectedFormId(selectedPage.selected_form_id ?? selectedPage.form_id ?? null);
      setEnabled(selectedPage.is_enabled);
    }
  }, [selectedPage?.page_id, selectedPage?.selected_form_id, selectedPage?.form_id, selectedPage?.is_enabled]);

  useEffect(() => {
    const oauth = searchParams.get("oauth");
    if (oauth === "success") {
      refetchConfig();
      toast({ title: "Connected", description: "Meta account connected. Select a page and form below." });
      setSearchParams({}, { replace: true });
    } else if (oauth === "error") {
      const err = searchParams.get("error") || "Connection failed";
      toast({ title: "Meta connection failed", description: err, variant: "destructive" });
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, refetchConfig, setSearchParams]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if (event.data?.type === "META_OAUTH_SUCCESS") {
        refetchConfig();
        toast({ title: "Connected", description: "Meta account connected." });
      }
    };
    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
  }, [refetchConfig]);

  const handleConnectWithMeta = useCallback(() => {
    oauthStart.mutate(undefined, {
      onSuccess: (url) => {
        const w = window.open(url, "meta-oauth", "width=600,height=700,scrollbars=yes");
        if (!w) {
          window.location.href = url;
        }
      },
      onError: (e) =>
        toast({
          title: "Meta integration failed",
          description: errorMessage(e),
          variant: "destructive",
        }),
    });
  }, [oauthStart]);

  const handleLoadForms = useCallback(() => {
    if (!selectedPageId) {
      toast({ title: "Meta integration failed", description: "Select a page first.", variant: "destructive" });
      return;
    }
    if (!activeWorkspaceId) return;
    loadForms.mutate(
      { page_id: selectedPageId, workspace_id: activeWorkspaceId },
      {
        onSuccess: (list) => {
          setForms(list);
          toast({ title: "Forms loaded", description: `${list.length} form(s) found.` });
        },
        onError: (e) =>
          toast({
            title: "Meta integration failed",
            description: errorMessage(e),
            variant: "destructive",
          }),
      }
    );
  }, [selectedPageId, activeWorkspaceId, loadForms]);

  const handleEnabledChange = useCallback(
    (checked: boolean) => {
      if (!selectedPageId) return;
      setEnabled(checked);
      subscribePage.mutate(
        { page_id: selectedPageId, enabled: checked },
        {
          onSuccess: () => {
            toast({ title: checked ? "Webhooks enabled" : "Webhooks disabled" });
            refetchConfig();
          },
          onError: (e) => {
            setEnabled(!checked);
            toast({
              title: "Meta integration failed",
              description: errorMessage(e),
              variant: "destructive",
            });
          },
        }
      );
    },
    [selectedPageId, subscribePage, refetchConfig]
  );

  const handleSave = useCallback(() => {
    if (!selectedPageId && !pageIdManual.trim()) {
      toast({
        title: "Meta integration failed",
        description: "Select a page first.",
        variant: "destructive",
      });
      return;
    }
    const pageIdToUse = selectedPageId || pageIdManual.trim();
    if (!activeWorkspaceId || !pageIdToUse) return;
    const tokenToUse = pageAccessToken.trim();
    saveConnection.mutate(
      {
        workspace_id: activeWorkspaceId,
        page_id: pageIdToUse,
        selected_form_id: selectedFormId || null,
        enabled,
        ...(tokenToUse ? { page_access_token: tokenToUse } : {}),
      },
      {
        onSuccess: () => {
          if (tokenToUse) setPageAccessToken("");
          toast({ title: "Saved", description: tokenToUse ? "Token stored securely." : "Selection saved." });
          refetchConfig();
        },
        onError: (e) =>
          toast({
            title: "Meta integration failed",
            description: errorMessage(e),
            variant: "destructive",
          }),
      }
    );
  }, [activeWorkspaceId, selectedPageId, pageIdManual, pageAccessToken, selectedFormId, enabled, saveConnection, refetchConfig]);

  const handleTestConnection = () => {
    if (!pageIdManual.trim() || !pageAccessToken.trim()) {
      toast({ title: "Page ID and token required", variant: "destructive" });
      return;
    }
    setConnectionError(null);
    testConnection.mutate(
      { page_id: pageIdManual.trim(), page_access_token: pageAccessToken.trim() },
      {
        onSuccess: (data) => {
          if (data?.ok) {
            setConnectionStatus("connected");
            toast({ title: "Connected", description: data.page?.name ?? "Token valid." });
          } else {
            setConnectionStatus("error");
            setConnectionError(data?.error?.message ?? "Invalid token or page.");
            toast({ title: "Connection failed", description: data?.error?.message, variant: "destructive" });
          }
        },
        onError: (e) => {
          setConnectionStatus("error");
          setConnectionError(errorMessage(e));
          toast({
            title: "Meta integration failed",
            description: errorMessage(e),
            variant: "destructive",
          });
        },
      }
    );
  };

  const handleSyncNow = () => {
    if (!selectedPageId) {
      toast({
        title: "Meta integration failed",
        description: "Select a page first.",
        variant: "destructive",
      });
      return;
    }
    syncNow.mutate(undefined, {
      onSuccess: (data) => {
        const inserted = data?.inserted ?? 0;
        const skipped = data?.skipped ?? 0;
        const errors = data?.errors ?? 0;
        const parts = [];
        if (inserted > 0) parts.push(`${inserted} imported`);
        if (skipped > 0) parts.push(`${skipped} skipped (already exist)`);
        if (errors > 0) parts.push(`${errors} errors`);
        toast({ title: "Sync complete", description: parts.length ? parts.join(" · ") : "Last sync updated." });
      },
      onError: (e) =>
        toast({
          title: "Meta integration failed",
          description: errorMessage(e),
          variant: "destructive",
        }),
    });
  };

  const canLoadForms = !!selectedPageId && !!activeWorkspaceId;
  const canUseStep3 = !!selectedPageId;
  const saveDisabled = !activeWorkspaceId || (!selectedPageId && !pageIdManual.trim());

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
          <SettingsIcon className="w-5 h-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Settings</h1>
          <p className="text-muted-foreground text-sm">Manage your workspace and integrations</p>
        </div>
      </div>

      <div className="flex gap-2 mb-8 border-b">
        {settingsTabs.map((tab) => (
          <Link
            key={tab.id}
            to={tab.path}
            className={cn(
              "flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors",
              tab.path === activeTabPath
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </Link>
        ))}
      </div>

      <div className="mb-6 flex items-center gap-2">
        <Link
          to="/app/settings/integrations"
          className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1"
        >
          <ArrowLeft className="w-4 h-4" />
          Integrations
        </Link>
        <span className="text-muted-foreground">/</span>
        <span className="text-sm font-medium flex items-center gap-2">
          <Facebook className="w-4 h-4" />
          Meta Lead Ads
        </span>
      </div>

      <div className="max-w-2xl space-y-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold">Connection wizard</h2>
          {isConnected && (
            <Badge variant="secondary" className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
              <Check className="w-3 h-3 mr-1" />
              Connected
            </Badge>
          )}
        </div>

        {/* Step 1: Connect + Page */}
        <div className="bg-card border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Wifi className="w-4 h-4" />
            Step 1 — Connect & select page
          </div>
          {!isConnected ? (
            <>
              <Button
                onClick={handleConnectWithMeta}
                disabled={oauthStart.isPending || !activeWorkspaceId}
              >
                {oauthStart.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                <Facebook className="w-4 h-4 mr-2" />
                Connect with Meta
              </Button>
              <p className="text-xs text-muted-foreground mt-1">
                Connect your Meta account to load pages.
              </p>
            </>
          ) : (
            <div>
              <Label>Page</Label>
              <Select
                value={selectedPageId ?? ""}
                onValueChange={(v) => {
                  setSelectedPageId(v || null);
                  setForms([]);
                }}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select a page" />
                </SelectTrigger>
                <SelectContent>
                  {configRows.map((r) => (
                    <SelectItem key={r.page_id} value={r.page_id}>
                      {r.page_name || r.page_id}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                Connected via OAuth. To add another account, use Advanced below or reconnect.
              </p>
            </div>
          )}
        </div>

        {/* Step 2: Forms — disabled until a page is selected */}
        <div className="bg-card border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <FileText className="w-4 h-4" />
            Step 2 — Forms
          </div>
          {!selectedPageId && (
            <p className="text-xs text-muted-foreground">
              Connect your Meta account to load pages.
            </p>
          )}
          <Button
            onClick={handleLoadForms}
            disabled={loadForms.isPending || !canLoadForms}
            variant="outline"
            size="sm"
          >
            {loadForms.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Load forms
          </Button>
          <div>
            <Label>Form (optional)</Label>
            <Select
              value={selectedFormId ?? "all"}
              onValueChange={(v) => setSelectedFormId(v === "all" ? null : v)}
            >
              <SelectTrigger className="mt-1" disabled={!selectedPageId}>
                <SelectValue placeholder="All forms (recommended)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All forms (recommended)</SelectItem>
                {forms.map((f) => (
                  <SelectItem key={f.id} value={f.id}>
                    {f.name || f.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Step 3: Sync & Enable — disabled until a page is selected */}
        <div className="bg-card border rounded-lg p-6 space-y-4">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Play className="w-4 h-4" />
            Step 3 — Sync & enable
          </div>
          {!selectedPageId && (
            <p className="text-xs text-muted-foreground">
              Select a page above to subscribe to webhooks or sync leads.
            </p>
          )}
          <div className="flex items-center justify-between">
            <Label htmlFor="is-enabled">Subscribe to leadgen webhooks</Label>
            <Switch
              id="is-enabled"
              checked={enabled}
              onCheckedChange={handleEnabledChange}
              disabled={!canUseStep3 || subscribePage.isPending}
            />
          </div>
          {lastSyncAt && (
            <p className="text-sm text-muted-foreground">Last sync: {lastSyncAt}</p>
          )}
          <Button
            onClick={handleSyncNow}
            disabled={syncNow.isPending || !isConnected || !canUseStep3}
            variant="outline"
            size="sm"
          >
            {syncNow.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            <RefreshCw className="w-4 h-4 mr-2" />
            Sync now
          </Button>
        </div>

        {/* Save */}
        <div className="flex items-center gap-2">
          <Button onClick={handleSave} disabled={saveConnection.isPending || saveDisabled}>
            {saveConnection.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save
          </Button>
          <Button variant="ghost" size="sm" onClick={() => refetchConfig()} disabled={configLoading}>
            {configLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Refresh status
          </Button>
        </div>

        {/* Advanced: manual token */}
        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen}>
          <CollapsibleTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2">
              {advancedOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
              Advanced: manual token
            </Button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="bg-muted/50 border rounded-lg p-4 mt-2 space-y-4">
              <div>
                <Label htmlFor="manual-page-id">Page ID</Label>
                <Input
                  id="manual-page-id"
                  value={pageIdManual}
                  onChange={(e) => setPageIdManual(e.target.value)}
                  placeholder="123456789012345"
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="manual-token">Page Access Token</Label>
                <Input
                  id="manual-token"
                  type="password"
                  value={pageAccessToken}
                  onChange={(e) => setPageAccessToken(e.target.value)}
                  placeholder="EAA..."
                  className="mt-1"
                />
              </div>
              <div className="flex items-center gap-2">
                <Button
                  onClick={handleTestConnection}
                  disabled={testConnection.isPending || !pageIdManual.trim() || !pageAccessToken.trim()}
                  variant="outline"
                  size="sm"
                >
                  {testConnection.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Test connection
                </Button>
                {connectionStatus === "connected" && (
                  <span className="text-sm text-green-600 dark:text-green-400">Connected</span>
                )}
                {connectionStatus === "error" && connectionError && (
                  <span className="text-sm text-destructive">{connectionError}</span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Save with token above to store it securely (optional if already connected via OAuth).
              </p>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {configLoading && (
          <div className="flex items-center gap-2 text-muted-foreground text-sm">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading...
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          Webhook URL for Meta App:{" "}
          <code className="block mt-1 bg-muted p-2 rounded text-xs break-all">
            {import.meta.env.VITE_SUPABASE_URL}/functions/v1/meta-webhook
          </code>
        </p>
      </div>
    </div>
  );
}
